import { Play, Volume2, VolumeX } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Slider } from "@/components/ui/Slider";
import { Toggle } from "@/components/ui/Toggle";
import { tone, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";
import { useSoundPreferences } from "@/lib/audio/useSoundPreferences";
import { onVoicesChanged, voiceFor } from "@/lib/audio/speech";
import { playCue, unlockAudio } from "@/lib/audio/tones";
import { useI18n, LOCALES } from "@/lib/i18n";

export function SoundSettings({ tone: which = "stage" }: { tone?: Tone | undefined }) {
  const { preferences, set } = useSoundPreferences();
  const { t, locale } = useI18n();
  const styles = tone(which);

  const [, revoiced] = useState(0);
  useEffect(
    () =>
      onVoicesChanged(() => {
        revoiced((count) => count + 1);
      }),
    [],
  );
  const voice = voiceFor(locale);

  const language = LOCALES.find((entry) => entry.id === locale)?.label ?? locale;
  const muted = preferences.volume === 0;

  return (
    <div className="flex flex-col gap-3">
      <Toggle
        checked={preferences.cues}
        description={t("sound.cuesBody")}
        label={t("sound.cues")}
        onChange={(next) => {
          set("cues", next);

          if (next) {
            unlockAudio();
            playCue("test", { force: true });
          }
        }}
        tone={which}
      />

      <Toggle
        checked={preferences.speech}
        description={t("sound.speechBody")}
        label={t("sound.speech")}
        onChange={(next) => {
          set("speech", next);
        }}
        tone={which}
      />

      {preferences.speech && voice === null ? (
        <p className={cn("text-xs", styles.muted)}>{t("sound.noVoice", { language })}</p>
      ) : null}

      <Slider
        icon={
          muted ? (
            <VolumeX aria-hidden className={cn("h-4 w-4 shrink-0", styles.muted)} />
          ) : (
            <Volume2 aria-hidden className={cn("h-4 w-4 shrink-0", styles.muted)} />
          )
        }
        label={t("sound.volume")}
        max={100}
        maxLabel={t("sound.volumeMax")}
        min={0}
        minLabel={t("sound.volumeMin")}
        onChange={(value) => {
          set("volume", value / 100);
        }}
        readout={
          muted
            ? t("sound.volumeMin")
            : t("sound.percent", { value: Math.round(preferences.volume * 100) })
        }
        step={5}
        tone={which}
        trailing={
          <Button
            onClick={() => {
              unlockAudio();
              playCue("test", { force: true });
            }}
            size="sm"
            tone={which}
            variant="secondary"
          >
            <Play aria-hidden className="h-3.5 w-3.5" />
            {t("sound.test")}
          </Button>
        }
        value={Math.round(preferences.volume * 100)}
      />
    </div>
  );
}
